import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {BootstrapIcon, HeaderService} from "../../../../../services/header.service";
import {ApiService} from "../../../../../services/api.service";
import {AiConfiguration} from "../../../../../shared/interfaces/ai-configuration";
import {MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatInput} from "@angular/material/input";
import {RouterLink} from "@angular/router";

@Component({
  selector: 'app-ai-configuration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatLabel, MatFormField, MatHint, MatInput, MatOption, MatSelect, RouterLink],
  templateUrl: './ai-configuration.component.html',
  styleUrl: './ai-configuration.component.scss'
})
export class AiConfigurationComponent implements OnInit {
  private headerService = inject(HeaderService);
  private apiService = inject(ApiService);
  private fb = inject(FormBuilder);

  configForm!: FormGroup;
  configs = signal<AiConfiguration[]>([])
  currentConfig = signal<AiConfiguration | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  protected average_tokens_per_request = signal<number>(800);
  protected average_cost_per_request = computed(() => {
    return this.average_tokens_per_request() * this.currentConfig()?.cost_per_1M_tokens! / 1_000_000 || 0;
  })

  ngOnInit(): void {
    this.headerService.setHeaderIcon(BootstrapIcon.ai);
    this.headerService.setHeaderTitle("KI-Konfiguration");
    this.headerService.setHeaderSubTitle('');

    this.initForm();
    this.loadConfiguration();

    this.configForm.get('model_name')?.valueChanges.subscribe((value: string) => {
        const cfg = this.configs().find(c => c.model_name === value) || null;
        this.currentConfig.set(cfg);
        if (cfg) {
          this.configForm.patchValue({
            system_prompt: cfg.system_prompt || '',
            temperature: cfg.temperature || 0.7,
            max_tokens: cfg.max_tokens || 1000,
            cost_per_1M_tokens: cfg.cost_per_1M_tokens || 0,
          }, {emitEvent: false})
        }
      }
    )
  }

  private initForm(): void {
    this.configForm = this.fb.group({
      model_name: ['', [Validators.required]],
      system_prompt: ['', [Validators.required]],
      temperature: [0.7, [Validators.required, Validators.min(0), Validators.max(2)]],
      max_tokens: [1000, [Validators.required, Validators.min(1)]]
    });
  }

  private loadConfiguration(): void {
    this.currentConfig.set(null);
    this.isLoading.set(true);
    this.apiService.getAiConfigurations().subscribe({
      next: (configs) => {
        this.configs.set(configs);
        this.currentConfig.set(configs.filter(c => c.is_active)[0]);
        this.configForm.patchValue({
          model_name: this.currentConfig()?.model_name || '',
          system_prompt: this.currentConfig()?.system_prompt || '',
          temperature: this.currentConfig()?.temperature || 0.7,
          max_tokens: this.currentConfig()?.max_tokens || 1000,
          cost_per_1M_tokens: this.currentConfig()?.cost_per_1M_tokens || 0,
        });
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Konfiguration:', error);
        this.errorMessage.set('Keine Konfiguration gefunden. Bitte erstellen Sie eine neue.');
        this.isLoading.set(false);
      }
    });
  }

  onSubmit(): void {
    if (this.configForm.invalid) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const formValue = this.configForm.value;

    this.apiService.updateAiConfiguration({...formValue, id: this.currentConfig()!.id, is_active: true}).subscribe({
      next: (config) => {
        this.currentConfig.set(config);
        this.successMessage.set('Konfiguration erfolgreich gespeichert!');
        this.isSaving.set(false);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (error) => {
        console.error('Fehler beim Speichern:', error);
        this.errorMessage.set('Fehler beim Speichern der Konfiguration.');
        this.isSaving.set(false);
      }
    });
  }

  resetForm(): void {
    if (this.currentConfig()) {
      this.configForm.patchValue({
        model_name: this.currentConfig()?.model_name,
        system_prompt: this.currentConfig()?.system_prompt,
        temperature: this.currentConfig()?.temperature || 0.7,
        max_tokens: this.currentConfig()?.max_tokens || 1000
      });
    } else {
      this.configForm.reset({
        temperature: 0.7,
        max_tokens: 1000
      });
    }
  }
}
